<template>
  <div id="content" class="text-left">
    <h1>Mon diagnostic gaspillage</h1>

    <!-- Aucune cantine n'est choisie à la place de l'utilisateur quand il en a plusieurs : un
         diagnostic engage un établissement précis. -->
    <div v-if="canteens.length > 1" class="canteen-picker">
      <label for="waste-diagnostic-canteen">Établissement</label>
      <select id="waste-diagnostic-canteen" v-model="canteenId" @change="load">
        <option v-for="canteen in canteens" :key="canteen.id" :value="canteen.id">{{ canteen.name }}</option>
      </select>
    </div>

    <p v-if="isLoading" role="status">Chargement de votre diagnostic…</p>

    <v-alert v-else-if="hasError" type="error" text role="alert">
      Le diagnostic n'a pas pu être chargé. Réessayez dans un instant.
    </v-alert>

    <v-form v-else-if="questionnaires.length" ref="form" id="diagnostic-form" @submit.prevent class="mb-4">
      <section v-for="block in questionnaires" :key="block.key" :aria-labelledby="`questionnaire-${block.key}`">
        <h2 :id="`questionnaire-${block.key}`" class="questionnaire-name">{{ block.name }}</h2>
        <p class="questionnaire-description">{{ block.description }}</p>

        <template v-for="question in block.questions">
          <div v-if="isVisible(question)" :key="question.key" class="question">
            <h3 class="question-label">{{ question.label }}</h3>

            <!-- Question déduite : elle est affichée comme un constat, jamais comme un champ.
                 « Jamais redemandé quand des pesées couvrent déjà la période » (§10.5 volet B) se
                 traduit ici littéralement — et le fondement est cité, pour que la réponse reste
                 remontable jusqu'à ses mesures. -->
            <template v-if="!question.askable">
              <p class="question-derived">
                <strong>{{ question.value }}</strong>
                — déduit de vos mesures : {{ question.basisLabel }}
              </p>
            </template>

            <DsfrRadio
              v-else-if="question.nature === 'boolean'"
              v-model="answers[question.key]"
              :items="booleanItems"
              label="Sélectionnez une option"
              hide-details
              class="text-left"
            />

            <DsfrTextField
              v-else
              v-model="answers[question.key]"
              label="Entrez le pourcentage"
              append-icon="mdi-percent"
              :rules="[validators.isPercentageOrEmpty]"
              class="text-left"
            />
          </div>
        </template>
      </section>

      <!-- L'indicateur réellement mesuré, rendu à côté du pourcentage déclaratif. Il n'en tient
           pas lieu : un pourcentage suppose un dénominateur — la masse préparée — que la collecte
           ne porte pas. Afficher la mesure aide à répondre sans inventer le calcul. -->
      <v-alert v-if="measurement && measurement.wastePerMealG" type="info" text class="mt-2">
        Vos campagnes mesurent {{ measurement.wastePerMealG }} g de déchets alimentaires par repas
        <span v-if="measurement.edibleWastePerMealG">, dont {{ measurement.edibleWastePerMealG }} g de gaspillage</span>
        .
      </v-alert>

      <p v-if="hasSaved" class="save-confirmation" role="status">
        Votre diagnostic est enregistré.
      </p>

      <v-alert v-if="saveError" type="error" text role="alert" class="mt-2">{{ saveError }}</v-alert>

      <div class="actions">
        <v-btn large color="primary" @click="submitForm" :disabled="!formIsValid || isSaving" class="my-8">
          {{ isSaving ? "Enregistrement…" : "Enregistrer mon diagnostic" }}
        </v-btn>

        <!-- Pas de bouton désactivé qui sortirait du parcours clavier : le lien vers la
             restitution n'apparaît qu'une fois le diagnostic enregistré (RGAA 7.1). -->
        <v-btn v-if="hasSaved" large outlined color="primary" :to="{ name: 'WasteDiagnosticResumePage' }" class="my-8">
          Voir ma restitution
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import Constants from "@/constants"
import DsfrRadio from "@/components/DsfrRadio"
import DsfrTextField from "@/components/DsfrTextField"
import validators from "@/validators"

export default {
  name: "WasteDiagnosticPage",
  components: {
    DsfrRadio,
    DsfrTextField,
  },
  data() {
    return {
      canteenId: null,
      // L'année du diagnostic est celle en cours : un diagnostic se passe sur son exercice, et
      // laisser choisir l'année ouvrirait la porte à répondre pour une année révolue.
      year: new Date().getFullYear(),
      diagnostic: null,
      answers: {},
      status: Constants.LoadingStatus.IDLE,
      isSaving: false,
      hasSaved: false,
      saveError: null,
    }
  },
  computed: {
    validators() {
      return validators
    },
    booleanItems() {
      return [
        { value: "Oui", label: "Oui" },
        { value: "Non", label: "Non" },
      ]
    },
    canteens() {
      return this.$store.state.userCanteenPreviews || []
    },
    isLoading() {
      return this.status === Constants.LoadingStatus.LOADING
    },
    hasError() {
      return this.status === Constants.LoadingStatus.ERROR
    },
    questionnaires() {
      return (this.diagnostic && this.diagnostic.questionnaires) || []
    },
    measurement() {
      return (this.diagnostic && this.diagnostic.measurement) || null
    },
    // À plat, pour raisonner sur les questions sans reparcourir les deux questionnaires.
    allQuestions() {
      return this.questionnaires.reduce((questions, block) => questions.concat(block.questions), [])
    },
    formIsValid() {
      const answered = (value) => value !== "" && value !== null && value !== undefined
      return this.allQuestions
        .filter((question) => question.askable && this.isVisible(question))
        .every((question) => answered(this.answers[question.key]))
    },
  },
  methods: {
    // Une question conditionnelle ne s'affiche que si sa condition est remplie — soit par une
    // réponse en cours de saisie, soit par ce que la collecte a déduit.
    isVisible(question) {
      if (!question.askedWhen) return true
      const driver = this.allQuestions.find((other) => other.key === question.askedWhen.question)
      const value = this.answers[question.askedWhen.question] || (driver && !driver.askable && driver.value)
      return value === question.askedWhen.equals
    },
    // L'état repart toujours de ce que le serveur a rendu : une valeur déduite n'entre pas dans
    // les réponses saisissables, elle n'a pas à repartir au POST — il la refuserait.
    applyDiagnostic(diagnostic) {
      this.diagnostic = diagnostic
      this.answers = {}
      this.allQuestions.forEach((question) => {
        if (question.askable && question.value !== null) this.$set(this.answers, question.key, question.value)
      })
    },
    load() {
      if (!this.canteenId) return Promise.resolve()
      this.status = Constants.LoadingStatus.LOADING
      this.hasSaved = false
      this.saveError = null
      return this.$store
        .dispatch("fetchWasteDiagnostic", { canteenId: this.canteenId, year: this.year })
        .then((response) => {
          this.applyDiagnostic(response)
          this.status = Constants.LoadingStatus.SUCCESS
        })
        .catch(() => {
          this.status = Constants.LoadingStatus.ERROR
        })
    },
    submitForm() {
      const answers = this.allQuestions
        .filter((question) => question.askable && this.isVisible(question))
        .map((question) => ({ question: question.key, value: this.answers[question.key] }))

      this.isSaving = true
      this.saveError = null
      return this.$store
        .dispatch("saveWasteDiagnostic", { canteenId: this.canteenId, year: this.year, answers })
        .then((response) => {
          this.applyDiagnostic(response)
          this.hasSaved = true
        })
        .catch(() => {
          this.saveError = "Votre diagnostic n'a pas pu être enregistré. Réessayez dans un instant."
        })
        .finally(() => {
          this.isSaving = false
        })
    },
  },
  mounted() {
    const loggedUser = this.$store.state.loggedUser
    if (loggedUser && loggedUser.canteen) this.canteenId = loggedUser.canteen.id
    else if (this.canteens.length) this.canteenId = this.canteens[0].id
    this.load()
  },
}
</script>

<style scoped>
#content {
  width: 210mm;
}

#diagnostic-form {
  display: flex;
  flex-direction: column;
  align-items: left;
  max-width: 1500px !important;
  margin: auto;
}

::v-deep span.fr-hint-text {
  display: none !important;
}

.canteen-picker {
  margin: 16px 0;
}

.canteen-picker label {
  margin-right: 8px;
  font-weight: 500;
}

.questionnaire-name {
  font-size: 1.4rem;
  font-weight: bold;
  margin-top: 2rem;
  margin-bottom: 0.25rem;
}

.questionnaire-description {
  margin-bottom: 1rem;
}

.question {
  margin-bottom: 1.5rem;
}

.question-label {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.question-derived {
  margin-bottom: 0;
}

.save-confirmation {
  margin-top: 1rem;
  margin-bottom: 0;
}

.actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.text-left {
  text-align: left;
}

@media (max-width: 210mm) {
  #content {
    width: 100%;
  }
}
</style>
